import { ref, watch, onMounted, onUnmounted } from 'vue'

const STORAGE_KEY = 'a11y-preferences'

export type FontSize = 'small' | 'medium' | 'large'

interface A11yPreferences {
  fontSize: FontSize
  highContrast: boolean
  reducedMotion: boolean
}

const FONT_SIZE_MAP: Record<FontSize, string> = {
  small:  '14px',
  medium: '16px',
  large:  '19px',
}

function loadPrefs(): A11yPreferences {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') ?? {
      fontSize: 'medium',
      highContrast: false,
      reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    }
  } catch {
    return { fontSize: 'medium', highContrast: false, reducedMotion: false }
  }
}

function savePrefs(prefs: A11yPreferences) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs))
}

function applyPrefs(prefs: A11yPreferences) {
  const html = document.documentElement
  // Font size
  html.style.setProperty('--a11y-base-font-size', FONT_SIZE_MAP[prefs.fontSize])
  // High contrast
  if (prefs.highContrast) {
    html.classList.add('high-contrast')
  } else {
    html.classList.remove('high-contrast')
  }
  // Reduced motion
  if (prefs.reducedMotion) {
    html.classList.add('reduce-motion')
  } else {
    html.classList.remove('reduce-motion')
  }
}

export function useAccessibility(opts?: {
  onOpenSearch?: () => void
}) {
  const prefs = ref<A11yPreferences>(loadPrefs())
  applyPrefs(prefs.value)

  watch(prefs, (p) => {
    applyPrefs(p)
    savePrefs(p)
  }, { deep: true })

  // ── Atajos de teclado globales (tarea 15.5.3) ──────────────────────────────
  function handleKeydown(e: KeyboardEvent) {
    // Ctrl+K — abrir búsqueda global
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault()
      opts?.onOpenSearch?.()
    }
  }

  onMounted(() => document.addEventListener('keydown', handleKeydown))
  onUnmounted(() => document.removeEventListener('keydown', handleKeydown))

  function setFontSize(size: FontSize) { prefs.value.fontSize = size }
  function toggleHighContrast() { prefs.value.highContrast = !prefs.value.highContrast }
  function toggleReducedMotion() { prefs.value.reducedMotion = !prefs.value.reducedMotion }
  function resetPrefs() {
    prefs.value = { fontSize: 'medium', highContrast: false, reducedMotion: false }
  }

  return { prefs, setFontSize, toggleHighContrast, toggleReducedMotion, resetPrefs }
}
